import { BreadcrumbHeading, SecondaryButton, Spinner, Typography } from '@weareopenr/component-library';
import styles from './ProductPublicationStatus.module.scss';
import { ReactNode, useMemo, useState } from 'react';
import { formatDate } from '../../helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import dayjs from 'dayjs';
import { Routes } from 'src/routes';
import { useEvent } from 'src/hooks/useEvent';
import { IEvent, IEventObject, EVENT_TYPES, ENTITY_TYPES, EVENT_STATUSES, NotificationStatus } from 'src/api/getEvents';
import { TEntityType } from 'src/types/menu.types';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { capitalise } from 'src/helpers/capitalise';
import { formatStatus } from 'src/helpers/getPublicationStatus/getPublicationStatus';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { getPublishTypeLabel } from 'src/helpers/getPublishTypeLabel/getPublishTypeLabel';
import { PublishType } from 'src/components/ProductPublishModal/types';
import { StatusIcon } from 'src/components/StatusIcon';
import { getMenuVenueCounts } from 'src/utils/schedules/getMenuVenueCounts';
import { InfoTooltip, InfoTooltipType } from 'src/components/InfoTooltip/InfoTooltip';
import { PublishReviewModal, ReviewExpiredAlert, getSystemNames } from 'src/components/PublishReviewModal';
import { PLAN_OBJECT_NAME, usePublishPlans } from 'src/hooks/usePublishPlans';
import { EventDownloads } from 'src/components/EventDownloads';

interface ExternalSystemGroup {
  name: string;
  productEvent?: IEvent;
  pricingEvent?: IEvent;
  menuEvent?: IEvent;
  timetableEvent?: IEvent;
}

const formatDateTime = (date: Date) => `${formatDate(date)}, ${formatTime(date)}`;

const STATUS_LINES: Partial<Record<IEvent['status'], string>> = {
  [EVENT_STATUSES.PENDING_REVIEW]: 'Awaiting review',
  [EVENT_STATUSES.CAUTION]: 'The external system changed since the preview. Publish again.',
};

// Rows have always formatted their status differently, so each keeps its own fallback.
const getStatusLine = (status: IEvent['status'], format: (status: string) => string = capitalise): string =>
  STATUS_LINES[status] ?? format(status);

const RowActionButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <SecondaryButton buttonStyle="outline" size="small" onClick={onClick} className={styles['review-button']}>
    {label}
  </SecondaryButton>
);

// Products and pricing are both published from the Product Library, so it's the fallback
const SOURCE_ROUTES: Partial<Record<TEntityType, Routes>> = {
  MENU: Routes.MENUS,
  TIMETABLE: Routes.TIMETABLES,
  MENU_SET: Routes.MENU_SETS,
};

const getSourcePath = ({ entity_type, entity_id }: IEvent): string => {
  const route = entity_type && SOURCE_ROUTES[entity_type];
  return route && entity_id ? `${route}/${entity_id}` : Routes.PRODUCT_LIBRARY;
};

const isExternalSystemEvent = (child: IEvent) =>
  (child.event_type === EVENT_TYPES.PUBLISH_PRICING ||
    child.event_type === EVENT_TYPES.PUBLISH_PRODUCT ||
    child.event_type === EVENT_TYPES.PUBLISH_MENU ||
    child.event_type === EVENT_TYPES.PUBLISH_TIMETABLE ||
    child.event_type === EVENT_TYPES.PUBLISH_MENU_SET) &&
  child.entity_type === ENTITY_TYPES.EXTERNAL_SYSTEM;

// The plan is opened from the review modal, so it isn't offered as a download
const getDownloadObjects = (objects?: IEventObject[]) => objects?.filter(({ name }) => name !== PLAN_OBJECT_NAME);

const getEventTime = ({ status, message, updated_at }: IEvent): string =>
  formatDateTime(
    new Date(status === NotificationStatus.SCHEDULED ? message.split('Publish scheduled for ')[1] : updated_at),
  );

interface IExternalSystemEventRowProps {
  name: string;
  event: IEvent;
  onRefreshObjects: () => Promise<IEventObject[] | undefined>;
  reviewAction?: ReactNode;
}

const ExternalSystemEventRow = ({ name, event, onRefreshObjects, reviewAction }: IExternalSystemEventRowProps) => (
  <div className={styles['item']}>
    <StatusIcon status={event.status} animate={event.status === NotificationStatus.PENDING} />
    <div>
      <Typography size="small" weight="bold">
        {name}
      </Typography>
      <Typography size="small">{getStatusLine(event.status)}</Typography>
      {event.message && event.status !== NotificationStatus.SCHEDULED && (
        <Typography size="small" className={styles['error-message']}>
          {event.message}
        </Typography>
      )}
      <Typography size="small" className={styles['event-time']}>
        {` ${getEventTime(event)}`}
      </Typography>
      <EventDownloads objects={getDownloadObjects(event.objects)} onRefresh={onRefreshObjects} />
      {reviewAction}
    </div>
  </div>
);

interface IMenuChannelSectionProps {
  channelName: string;
  menuEvent: IEvent;
  reviewAction?: ReactNode;
}

const MenuChannelSection = ({ channelName, menuEvent, reviewAction }: IMenuChannelSectionProps) => {
  const menuVenues = useMemo(
    () =>
      (menuEvent.child_events ?? []).filter(
        (e) => e.entity_type === ENTITY_TYPES.VENUE && e.status !== EVENT_STATUSES.UNTRACKED,
      ),
    [menuEvent],
  );
  const venueCounts = useMemo(() => getMenuVenueCounts(menuVenues), [menuVenues]);
  const isChannelFailure = menuEvent.status === EVENT_STATUSES.FAILURE;

  return (
    <>
      <div className={styles['item']}>
        <StatusIcon status={menuEvent.status} animate={menuEvent.status === NotificationStatus.PENDING} />
        <div>
          <Typography size="small" weight="bold">
            {channelName}
          </Typography>
          <Typography size="small" className={styles['menu-channel-summary']}>
            {`${channelName} - ${menuVenues.length} ${menuVenues.length === 1 ? 'venue' : 'venues'}`}
          </Typography>
          <Typography size="small">
            {`Success - ${venueCounts.success} ${pluralize('Venue', venueCounts.success)} | Pending - ${venueCounts.pending} ${pluralize('Venue', venueCounts.pending)} | Failed - ${venueCounts.failure} ${pluralize('Venue', venueCounts.failure)}`}
          </Typography>
          {isChannelFailure && menuEvent.message && (
            <Typography size="small" className={styles['error-message']}>
              {menuEvent.message}
            </Typography>
          )}
          {STATUS_LINES[menuEvent.status] && <Typography size="small">{getStatusLine(menuEvent.status)}</Typography>}
          {reviewAction}
        </div>
      </div>

      {menuVenues.length > 0 && (
        <div className={styles['venue-list']}>
          {menuVenues.map((venueEvent) => {
            const isFailure = venueEvent.status === EVENT_STATUSES.FAILURE;
            const statusLine =
              isFailure && venueEvent.message ? venueEvent.message : getStatusLine(venueEvent.status, formatStatus);

            return (
              <div key={venueEvent.event_id} className={styles['item']}>
                <StatusIcon status={venueEvent.status} animate={venueEvent.status === NotificationStatus.PENDING} />
                <div>
                  <Typography size="small" weight="bold">
                    {venueEvent.entity_name}
                  </Typography>
                  <Typography size="small">{statusLine}</Typography>
                  <Typography size="small" className={styles['event-time']}>
                    {`Last Updated - ${formatDateTime(new Date(venueEvent.updated_at))}`}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export const ProductPublicationStatus = () => {
  const { eventId } = useParams<{ eventId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: event, isLoading: loading, refetch } = useEvent(eventId);
  const refreshChildObjects = (childEventId: string) => async () => {
    const { data } = await refetch({ throwOnError: true, cancelRefetch: false });
    return data?.child_events?.find((child) => child.event_id === childEventId)?.objects;
  };
  const entityNameFromHistory = (location.state as { entityName?: string | null } | null)?.entityName;

  const publishPlans = usePublishPlans(event);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false);
  const isReviewRequested = new URLSearchParams(location.search).get('review') === 'true';

  const openReviewModal = () => setIsReviewModalOpen(true);
  const closeReviewModal = () => {
    setIsReviewModalOpen(false);
    navigate({ pathname: location.pathname, search: '' }, { replace: true, state: location.state });
  };
  const publishAgain = () => event && navigate(getSourcePath(event));

  const getReviewAction = ({ event_id, status }: IEvent): ReactNode => {
    if (status === EVENT_STATUSES.CAUTION) {
      return <RowActionButton label="Publish again" onClick={publishAgain} />;
    } else if (!publishPlans.reviewableEventIds.includes(event_id)) {
      return null;
    } else if (publishPlans.isExpired) {
      return (
        <div className={styles['review-alert']}>
          <ReviewExpiredAlert systemNames={getSystemNames(publishPlans.plans)} onPublishAgain={publishAgain} />
        </div>
      );
    } else {
      return <RowActionButton label="Review" onClick={openReviewModal} />;
    }
  };

  const lastPublishedDate = useMemo(() => {
    return event?.updated_at &&
      (event?.status === NotificationStatus.SENT || event.status === NotificationStatus.PARTIAL_SUCCESS)
      ? new Date(event.updated_at)
      : null;
  }, [event]);

  const externalSystemGroups = useMemo(() => {
    if (!event?.child_events) return new Map<string, ExternalSystemGroup>();

    const groups = new Map<string, ExternalSystemGroup>();
    const eventsBySystem = new Map<string, IEvent[]>();
    event.child_events
      .filter((childEvent) => isExternalSystemEvent(childEvent) && childEvent.status !== EVENT_STATUSES.UNTRACKED)
      .forEach((childEvent) => {
        if (!childEvent.entity_id) return;

        const existing = eventsBySystem.get(childEvent.entity_id);
        if (existing) {
          existing.push(childEvent);
        } else {
          eventsBySystem.set(childEvent.entity_id, [childEvent]);
        }
      });

    eventsBySystem.forEach((systemEvents, externalSystemId) => {
      const pricingEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_PRICING);
      const productEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_PRODUCT);
      // A Menu Set reaches each channel as one ordinary menu, so its channel events render as a menu's.
      const menuEvent = systemEvents.find(
        (e) => e.event_type === EVENT_TYPES.PUBLISH_MENU || e.event_type === EVENT_TYPES.PUBLISH_MENU_SET,
      );
      const timetableEvent = systemEvents.find((e) => e.event_type === EVENT_TYPES.PUBLISH_TIMETABLE);

      groups.set(externalSystemId, {
        name: systemEvents.find((e) => e.entity_name)?.entity_name ?? '',
        productEvent,
        pricingEvent,
        menuEvent,
        timetableEvent,
      });
    });

    return groups;
  }, [event]);

  const publishDetails = useMemo(() => {
    const isMenuPublish = event?.event_type === EVENT_TYPES.PUBLISH_MENU;
    const isTimetablePublish = event?.event_type === EVENT_TYPES.PUBLISH_TIMETABLE;
    const isMenuSetPublish = event?.event_type === EVENT_TYPES.PUBLISH_MENU_SET;
    const isPricingPublish =
      !isMenuPublish &&
      !isTimetablePublish &&
      !isMenuSetPublish &&
      event?.child_events?.find(isExternalSystemEvent)?.event_type === EVENT_TYPES.PUBLISH_PRICING;

    let priceSchemeEvent: IEvent | undefined;
    let publishType: string;

    switch (true) {
      case isMenuPublish:
        priceSchemeEvent = event?.child_events?.find((c) => c.entity_type === ENTITY_TYPES.PRICE_SCHEME);
        publishType = 'Menu Publish';
        break;
      case isMenuSetPublish:
        // The contract puts the PRICE_SCHEME log directly under the root, as for a menu.
        priceSchemeEvent = event?.child_events?.find((c) => c.entity_type === ENTITY_TYPES.PRICE_SCHEME);
        publishType = 'Menu Set Publish';
        break;
      case isPricingPublish:
        priceSchemeEvent = event?.child_events
          ?.flatMap((e) => e.child_events ?? [])
          .find((c) => c.entity_type === ENTITY_TYPES.PRICE_SCHEME);
        publishType = getPublishTypeLabel(PublishType.PRICING).title;
        break;
      case isTimetablePublish:
        // A timetable can resolve to several menu/price-scheme pairs, not one - there's no
        // single price scheme to summarise here the way there is for a menu/pricing publish.
        publishType = 'Menu & Pricing Timetable';
        break;
      default:
        publishType = getPublishTypeLabel(PublishType.PRODUCT).title;
    }

    const snapshotDate = event?.created_at ? dayjs(event.created_at).format('DD/MM/YY, HH:mm') : null;

    return {
      publishType,
      publishDate: event?.created_at ? dayjs(event.created_at).format('ddd, D MMM, HH:mm') : null,
      isPricingPublish,
      isMenuPublish,
      isTimetablePublish,
      isMenuSetPublish,
      priceScheme: priceSchemeEvent?.entity_name ?? null,
      entityName: event?.entity_name ?? entityNameFromHistory ?? null,
      snapshotTooltip: snapshotDate
        ? `This timetable was published using a snapshot of the menu and pricing data at ${snapshotDate}`
        : null,
    };
  }, [event, entityNameFromHistory]);

  const showsEntityName =
    publishDetails.isMenuPublish || publishDetails.isTimetablePublish || publishDetails.isMenuSetPublish;

  let subtitle: string | null = null;
  if (showsEntityName) {
    subtitle = event?.status ? `Publish status - ${formatStatus(event.status)}` : null;
  } else if (lastPublishedDate) {
    subtitle = `Last published: ${formatDateTime(lastPublishedDate)}`;
  }

  return (
    <>
      {eventId && (publishPlans.hasPendingReview || publishPlans.decision) && (
        <PublishReviewModal
          isOpen={isReviewModalOpen || isReviewRequested}
          rootEventId={eventId}
          plans={publishPlans.plans}
          isLoading={publishPlans.isLoading}
          isError={publishPlans.isError}
          isExpired={publishPlans.isExpired}
          decision={publishPlans.decision}
          closeModal={closeReviewModal}
          onPublishAgain={publishAgain}
        />
      )}
      <BreadcrumbHeading
        breadcrumbs={[
          location.pathname.startsWith(Routes.PUBLISH_HISTORY)
            ? { label: 'Publish History', path: Routes.PUBLISH_HISTORY }
            : { label: 'Product Library', path: Routes.PRODUCT_LIBRARY },
          {
            label: (showsEntityName && publishDetails.entityName) || 'Publication Status',
            path: location.pathname,
          },
        ]}
        subtitle={subtitle}
      />
      {loading ? (
        <div className={styles['spinner']}>
          <Spinner size="xlarge" colour="rainbow" data-testid="spinner" />
        </div>
      ) : (
        <div className={styles['publication-status']}>
          <div className={styles['publish-details']}>
            <Typography size="xlarge" weight="semi-bold">
              Publish Details
            </Typography>
            <div className={styles['publish-details-grid']}>
              {[
                { label: 'Publish Type', value: publishDetails.publishType, tooltip: null },
                { label: 'Publish Date', value: publishDetails.publishDate ?? '—', tooltip: null },
                ...(publishDetails.isTimetablePublish
                  ? [
                      {
                        label: 'Timetable',
                        value: publishDetails.entityName ?? '—',
                        tooltip: publishDetails.snapshotTooltip,
                      },
                    ]
                  : []),
                ...(publishDetails.isPricingPublish || publishDetails.isMenuPublish || publishDetails.isMenuSetPublish
                  ? [{ label: 'Price Scheme', value: publishDetails.priceScheme ?? '—', tooltip: null }]
                  : []),
              ].map(({ label, value, tooltip }) => (
                <div key={label} className={styles['publish-detail']}>
                  <div className={styles['label-row']}>
                    <Typography weight="bold">{label}</Typography>
                    {tooltip && (
                      <span className={styles['info-icon']}>
                        <InfoTooltip type={InfoTooltipType.TIMETABLE_SNAPSHOT} text={tooltip} />
                      </span>
                    )}
                  </div>
                  <Typography>{value}</Typography>
                </div>
              ))}
            </div>
          </div>
          <div className={styles['status-container']}>
            {Array.from(externalSystemGroups.entries()).map(([externalSystemId, group]) => (
              <div key={externalSystemId} className={styles['external-system-group']}>
                <div className={styles['events']}>
                  {group.productEvent && (
                    <ExternalSystemEventRow
                      name={group.name}
                      event={group.productEvent}
                      onRefreshObjects={refreshChildObjects(group.productEvent.event_id)}
                      reviewAction={getReviewAction(group.productEvent)}
                    />
                  )}

                  {group.pricingEvent && (
                    <>
                      <ExternalSystemEventRow
                        name={group.name}
                        event={group.pricingEvent}
                        onRefreshObjects={refreshChildObjects(group.pricingEvent.event_id)}
                        reviewAction={getReviewAction(group.pricingEvent)}
                      />

                      {group.pricingEvent.child_events && group.pricingEvent.child_events.length > 0 && (
                        <div className={styles['venue-list']}>
                          {group.pricingEvent.child_events
                            .filter(
                              (e) => e.entity_type === ENTITY_TYPES.VENUE && e.status !== EVENT_STATUSES.UNTRACKED,
                            )
                            .map((venueEvent) => (
                              <div key={venueEvent.event_id} className={styles['item']}>
                                <StatusIcon
                                  status={venueEvent.status}
                                  animate={venueEvent.status === NotificationStatus.PENDING}
                                />
                                <div>
                                  <Typography size="small" weight="bold">
                                    {venueEvent.entity_name}
                                  </Typography>
                                  <Typography size="small">{getStatusLine(venueEvent.status)}</Typography>
                                  {venueEvent.message && (
                                    <Typography size="small" className={styles['error-message']}>
                                      {venueEvent.message}
                                    </Typography>
                                  )}
                                  <Typography size="small" className={styles['event-time']}>
                                    {`Last Updated - ${formatDateTime(new Date(venueEvent.updated_at))}`}
                                  </Typography>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}

                  {group.menuEvent && (
                    <MenuChannelSection
                      channelName={group.name}
                      menuEvent={group.menuEvent}
                      reviewAction={getReviewAction(group.menuEvent)}
                    />
                  )}
                  {group.timetableEvent && (
                    <MenuChannelSection
                      channelName={group.name}
                      menuEvent={group.timetableEvent}
                      reviewAction={getReviewAction(group.timetableEvent)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
